import { Injectable, OnDestroy } from '@angular/core';
import { ServerService } from 'app/core/services/server.service';
import { BehaviorSubject, Subject } from 'rxjs';
import {
    AlertConfiguration,
    AlertParameter,
    Notification,
    TriggerConfiguration,
    TriggerConfigurationUser
} from './alert.model';
import { UserService } from 'app/core/services/user.service';

@Injectable({
    providedIn: 'root'
})
export class AlertSettingsService implements OnDestroy {
    private configurationsSubject = new BehaviorSubject<AlertConfiguration[]>([]);
    configurations$ = this.configurationsSubject.asObservable();

    private userAlertsSubject = new BehaviorSubject<Notification[]>([]);
    userAlerts$ = this.userAlertsSubject.asObservable();

    private allAlertsSubject = new BehaviorSubject<Notification[]>([]);
    allAlerts$ = this.allAlertsSubject.asObservable();

    private foundNotificationSubject = new BehaviorSubject<Notification | null>(null);
    foundNotification$ = this.foundNotificationSubject.asObservable();

    private foundConfigurationSubject = new BehaviorSubject<AlertConfiguration | null>(
        null
    );
    foundConfiguration$ = this.foundConfigurationSubject.asObservable();

    private configurationsByIdSubject = new BehaviorSubject<any[]>([]);
    configurationsById$ = this.configurationsByIdSubject.asObservable();

    private notCreatedConfigurationsSubject = new BehaviorSubject<TriggerConfiguration[]>(
        []
    );
    notCreatedConfigurations$ = this.notCreatedConfigurationsSubject.asObservable();

    private ngUnsubscribe = new Subject<void>();
    private alertOffset = 0;
    private readonly alertLimit = 20;
    public totalAlertsNotClosed: number = 0;

    constructor(
        private serverService: ServerService,
        private userService: UserService
    ) {}

    getConfigurations(): void {
        const user = this.userService.user.user_id;
        if (!user) return;
        this.serverService.post(
            'alerts/getConfigurations',
            { user_id: user },
            (data: AlertConfiguration[]) => {
                // Defensively parse paramsConfiguration and params, which might be JSON strings
                const parsedData = data.map((config) => {
                    const newConfig = { ...config };
                    if (
                        newConfig.paramsConfiguration &&
                        typeof newConfig.paramsConfiguration === 'string'
                    ) {
                        try {
                            newConfig.paramsConfiguration = JSON.parse(
                                newConfig.paramsConfiguration
                            );
                        } catch (e) {
                            console.error(
                                `Failed to parse paramsConfiguration for ${newConfig.id}`,
                                e
                            );
                            newConfig.paramsConfiguration = null;
                        }
                    }
                    if (newConfig.params && typeof newConfig.params === 'string') {
                        try {
                            newConfig.params = JSON.parse(newConfig.params);
                        } catch (e) {
                            console.error(
                                `Failed to parse params for ${newConfig.id}`,
                                e
                            );
                            newConfig.params = [];
                        }
                    }
                    return newConfig;
                });
                this.configurationsSubject.next(parsedData);
            },
            (error) => {
                console.error('Error fetching alert configurations', error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    updateConfiguration(id: string, isActive: boolean, params: AlertParameter[]): void {
        const user = this.userService.user.user_id;
        if (!user) return;
        this.serverService.post(
            'alerts/updateConfigurations',
            { user_id: user, configuration: { id, isActive, params } },
            () => {
                // After update, refresh the configurations to get the latest state
                this.getConfigurations();
            },
            (error) => {
                console.error('Error updating alert configuration', error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    getUserAlerts(loadMore: boolean = false): void {
        const user = this.userService.user.user_id;
        if (!user) return;

        if (!loadMore) {
            // Reset for a fresh load
            this.alertOffset = 0;
        }

        this.serverService.post(
            'alerts/getUserAlerts',
            { user_id: user, limit: this.alertLimit, offset: this.alertOffset },
            (response: [Notification[], number]) => {
                if (!response) return;
                const newAlerts = response[0];
                this.totalAlertsNotClosed = response[1];

                // Increment the offset for the next fetch
                this.alertOffset += newAlerts.length;

                if (!loadMore) {
                    // This is a fresh load, replace the list
                    this.userAlertsSubject.next(newAlerts);
                } else {
                    // This is a "load more" call, append to the list
                    const currentAlerts = this.userAlertsSubject.getValue();
                    this.userAlertsSubject.next([...currentAlerts, ...newAlerts]);
                }
            },
            (error) => {
                console.error('Error fetching user alerts', error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    toggleAckForUser(id: number, callback?: () => void): void {
        const user = this.userService.user.user_id;
        if (!user) return;
        this.serverService.post(
            'alerts/toggleAckForUser',
            { user_id: user, id },
            () => {
                if (callback) {
                    callback();
                }
            },
            (error) => {
                console.error(`Error toggling ack for alert ${id}`, error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    ackAllForUser(type: 'all' | 'user' | 'system' | 'data', callback?: () => void): void {
        const user = this.userService.user.user_id;
        if (!user) return;
        this.serverService.post(
            'alerts/ackAllForUser',
            { user_id: user, type },
            () => {
                if (callback) {
                    callback();
                }
            },
            (error) => {
                console.error(`Error acknowledging all ${type} alerts`, error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    findAll(): void {
        this.serverService.get(
            'alerts/findAll',
            {},
            (data: Notification[]) => {
                this.allAlertsSubject.next(data);
            },
            (error) => {
                console.error('Error fetching all alerts', error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    find(id: number): void {
        this.serverService.get(
            'alerts/find',
            { id },
            (data: Notification) => {
                this.foundNotificationSubject.next(data);
            },
            (error) => {
                console.error(`Error finding alert ${id}`, error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    findConfiguration(id: number): void {
        const user = this.userService.user.user_id;
        if (!user) return;
        this.serverService.get(
            'alerts/findConfiguration',
            { user_id: user, id },
            (data: AlertConfiguration) => {
                this.foundConfigurationSubject.next(data);
            },
            (error) => {
                console.error(`Error finding configuration ${id}`, error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    findConfigurationsById(id: string): void {
        this.serverService.get(
            'alerts/findConfigurationsById',
            { id },
            (data: any[]) => {
                this.configurationsByIdSubject.next(data);
            },
            (error) => {
                console.error(`Error finding configurations by id ${id}`, error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    findNotificationConfigurationsNotCreated(): void {
        const user = this.userService.user.user_id;
        if (!user) return;
        this.serverService.get(
            'alerts/findNotificationConfigurationsNotCreated',
            { user_id: user },
            (data: TriggerConfiguration[]) => {
                this.notCreatedConfigurationsSubject.next(data);
            },
            (error) => {
                console.error('Error fetching not created configurations', error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    createNotificationConfigurations(configurations: TriggerConfigurationUser[]): void {
        this.serverService.post(
            'alerts/createNotificationConfigurations',
            { configurations },
            () => {
                // Optionally refresh or handle success
            },
            (error) => {
                console.error('Error creating notification configurations', error);
            },
            this.ngUnsubscribe,
            true
        );
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
